import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { getRxkSkus } from '@/lib/rxkSkus';
import ResellerLayout from '../../components/ResellerLayout';
import { sortProducts } from '@/lib/productOrder';

interface ProductOption {
  sku: string;
  name_en: string | null;
  msrp_bdt: number | null;
}

interface OrderLine {
  sku: string;
  qty: string;
}

export default function ResellerNewOrderPage() {
  const { resellerProfile } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [lines, setLines] = useState<OrderLine[]>([{ sku: '', qty: '1' }]);
  const [address, setAddress] = useState(resellerProfile?.district ? `${resellerProfile.district}, ` : '');
  const [phone, setPhone] = useState(resellerProfile?.contact ?? '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getRxkSkus()
      .then((skus) => supabase.from('core_products').select('sku, name_en, msrp_bdt').in('sku', skus).eq('status', 'active').order('sku'))
      .then(({ data }) => setProducts(sortProducts(data as ProductOption[])));
  }, []);

  const discountPct = resellerProfile?.wholesaleDiscountPct ?? 0;
  const wholesalePrice = (msrp: number | null) => Math.round((msrp ?? 0) * (1 - discountPct / 100));

  const updateLine = (idx: number, patch: Partial<OrderLine>) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { sku: '', qty: '1' }]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const subtotal = lines.reduce((sum, l) => {
    const product = products.find((p) => p.sku === l.sku);
    const qty = Number(l.qty) || 0;
    return sum + wholesalePrice(product?.msrp_bdt ?? null) * qty;
  }, 0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const items = lines.filter((l) => l.sku && Number(l.qty) > 0).map((l) => ({ sku: l.sku, qty: Number(l.qty) }));
    if (items.length === 0) {
      setError('Add at least one product');
      return;
    }
    if (!address.trim()) {
      setError('Delivery address is required');
      return;
    }
    setSubmitting(true);
    type PlaceResult = { ok?: boolean; order_no?: string; error?: string; details?: string };
    // Non-2xx responses arrive as an error whose body holds the details.
    const place = async (allowDuplicate: boolean): Promise<PlaceResult> => {
      const { data, error: invokeError } = await supabase.functions.invoke<PlaceResult>('core-place-reseller-order', {
        body: { items, allow_duplicate: allowDuplicate, customer_info: { phone, address: address.trim(), note: note.trim() } },
      });
      if (data) return data;
      const ctx = (invokeError as { context?: Response } | null)?.context;
      const body = ctx && typeof ctx.json === 'function' ? await ctx.json().catch(() => null) : null;
      return (body as PlaceResult | null) ?? { error: invokeError?.message };
    };
    let data = await place(false);
    if (data.error === 'possible_duplicate') {
      const proceed = window.confirm(`${data.details ?? 'You placed the same order recently.'}\n\nPlace another order with the same items anyway?`);
      if (!proceed) {
        setSubmitting(false);
        return;
      }
      data = await place(true);
    }
    setSubmitting(false);
    if (!data.ok) {
      setError(data.details || data.error || 'Failed to place order');
      return;
    }
    showToast(`Order ${data.order_no} placed!`);
    navigate('/reseller/orders');
  };

  return (
    <ResellerLayout title="Place Order">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E8E8E6] p-6 space-y-5">
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#8A8A8A]">Products</p>
          {lines.map((line, idx) => {
            return (
              <div key={idx} className="flex items-end gap-2">
                <div className="flex-1">
                  {idx === 0 && <label className="block text-[11px] text-[#8A8A8A] mb-1">Product</label>}
                  <select
                    required
                    value={line.sku}
                    onChange={(e) => updateLine(idx, { sku: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
                  >
                    <option value="">Select product…</option>
                    {products.map((p) => (
                      <option key={p.sku} value={p.sku}>
                        {p.name_en || p.sku} — ৳{wholesalePrice(p.msrp_bdt).toLocaleString()}
                        {discountPct > 0 && ` (was ৳${(p.msrp_bdt ?? 0).toLocaleString()})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-20">
                  {idx === 0 && <label className="block text-[11px] text-[#8A8A8A] mb-1">Qty</label>}
                  <input
                    type="number"
                    min={1}
                    required
                    value={line.qty}
                    onChange={(e) => updateLine(idx, { qty: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
                  />
                </div>
                {lines.length > 1 && (
                  <button type="button" onClick={() => removeLine(idx)} className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl text-[#8A8A8A] hover:text-[#7B1113] hover:bg-[#7B1113]/5 cursor-pointer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
          <button type="button" onClick={addLine} className="inline-flex items-center gap-1.5 text-xs text-[#7B1113] font-medium hover:underline cursor-pointer">
            <Plus className="w-3.5 h-3.5" /> Add another product
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-[#E8E8E6]">
          <div>
            <label className="block text-[11px] text-[#8A8A8A] mb-1">Phone</label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
            />
          </div>
          <div>
            <label className="block text-[11px] text-[#8A8A8A] mb-1">Delivery Address</label>
            <input
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] text-[#8A8A8A] mb-1">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] resize-none"
          />
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#E8E8E6]">
          <div>
            <p className="text-xs text-[#8A8A8A]">Subtotal{discountPct > 0 && ` (${discountPct}% wholesale discount applied)`}</p>
            <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontVariantNumeric: 'tabular-nums' }}>
              ৳{subtotal.toLocaleString()}
            </p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? 'Placing…' : 'Place Order'}
          </button>
        </div>
        {error && <p className="text-xs text-[#7B1113]">{error}</p>}
      </form>
    </ResellerLayout>
  );
}
